import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {RestSimpleResponseParser, RestYiiFilterService, RestYiiPagination, RestYiiSort} from 'gelios-ng-admin-components';
import {ProjectCountryService} from '../../project/project-country/project-country.service';
import {TranslateService} from '@ngx-translate/core';
import {ProjectBusinessLogicFilterService} from '../../shared/project-business-logic-filter/project-business-logic-filter.service';
import {idAll} from '../../shared/project-business-logic-filter/project-business-logic-filter-utils';

@Injectable({providedIn: 'root'})
export class StatisticsSelectionService {
  public selectedUserId: BehaviorSubject<any> = new BehaviorSubject<any>('all'); // = this.organisationFormControl.valueChanges;
  public selectedProjectId: BehaviorSubject<any> = new BehaviorSubject<any>(null); // = this.projectFormControl.valueChanges;
  public selectedProjectCountryCode: BehaviorSubject<any> = new BehaviorSubject<any>('all'); // = this.projectCountryFormControl.valueChanges;
  public selectedProjectCountryId: BehaviorSubject<any> = new BehaviorSubject<any>(null); // this.projectCountryFormControl.valueChanges;  = number
  public selectedRegionId: BehaviorSubject<any> = new BehaviorSubject<any>(null); // = this.projectRegionFormControl.valueChanges;
  projects: Observable<Array<any>> = new Observable<Array<any>>();
  countries: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  regions: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  users: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  selectedUserRole: Observable<string>;
  protected projectCountryService: ProjectCountryService;
  protected subscriptions: Subscription[] = [];

  constructor(
    protected http: HttpClient,
    protected translates: TranslateService,
    // protected immutableList: ImmutableListRepositoryService,
    protected projectBusinessLogicFilterService: ProjectBusinessLogicFilterService,
    @Inject('ENVIRONMENT_VERSION') environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
  ) {

    this.projectCountryService = new ProjectCountryService(
      http,
      new RestYiiFilterService(),
      new RestYiiSort(),
      new RestYiiPagination(),
      new RestSimpleResponseParser(),
      environmentVersion,
      environmentApiBasepoint
    );
    this.projectCountryService.fields = ['id', 'name', 'project_id', 'country_code', 'country.name'].join(',');
    this.projectCountryService.expand = ['country'].join(',');

    this.projects = combineLatest(
      this.projectBusinessLogicFilterService.projectList,
      this.projectBusinessLogicFilterService.selectedProjectId
    ).pipe(
      filter(
        ([projects, selectedProjectId]) =>
          projects !== null && selectedProjectId !== null
      ),
      map(
        ([projects, selectedProjectId]) => {
          // console.log(projects, selectedProjectId);
          const result = selectedProjectId === idAll ?
            projects
            : [projects.find(p => p.id === selectedProjectId)];
          // console.log(result);
          return result;
        }
      )
    );
    this.subscriptions.push(
      this.projects.subscribe(
        projects => {
          // console.log(projects);
          if (projects.length > 0) {
            this.selectedProjectId.next(projects[0].id);
          }
        }
      )
    );
    this.subscriptions.push(
      this.selectedProjectId.pipe(
        distinctUntilChanged(),
        filter(projectId => !!projectId),
        switchMap((projectId) => {
          const params = (new HttpParams())
            .append('ProjectCountrySearch[project_id]', projectId)
            .append('per-page', '250')
            .append('sort', 'country_code')
            .append('fields', 'id,name,project_id,country_code')
            .append('expand', 'country');
          return this.http.get(
            this.environmentApiBasepoint + 'project-country',
            {params: params}
          );
        }),
      ).subscribe(
        (countries: any) => {
          this.countries.next(countries);
          if (countries.length > 0) {
            this.selectedProjectCountryCode.next(countries[0].country_code);
            this.selectedProjectCountryId.next(countries[0].id);
          } else {
            this.selectedProjectCountryCode.next('all');
            this.selectedProjectCountryId.next('all');
          }
        }
      )
    );
    this.subscriptions.push(
      combineLatest(
        this.selectedProjectCountryId,
        this.selectedRegionId,
      ).pipe(
        filter(([countryId, regionId]) => !!countryId && !!regionId),
        distinctUntilChanged(
          (a, b) => a[0] === b[0] && a[1] === b[1]
        ),
        switchMap(([countryId, regionId]) => {
          let params = (new HttpParams())
            .append('CreatorSearch[role]', 'admin,integrator')
            .append('fields', 'id,name')
            .append('per-page', '250')
            .append('sort', 'login')
            .append('fields', 'id,login,role');
          if (regionId !== 'all') {
            params = params.append('CreatorSearch[region_id]', regionId);
          }
          if (countryId !== 'all') {
            params = params.append('CreatorSearch[project_country_id]', countryId);
          }
          return this.http.get(
            this.environmentApiBasepoint + 'creator',
            {params: params}
          );
        })
      ).pipe(
        map((users: Array<any>) => users.length > 1 ? [
            {
              id: 'all',
              login: this.translates.instant('All users'),
              role: 'admin'
            },
            ...users
          ]
          : users
        )
      ).subscribe(
        (users: Array<any>) => {
          this.users.next(users);
          const previouslySelectedUserId = this.selectedUserId.getValue();
          if (previouslySelectedUserId !== 'all') {
            const previouslySelectedUser = users.find(user => user.id === previouslySelectedUserId);
            if (previouslySelectedUser) {
              this.selectedUserId.next(previouslySelectedUserId);
            } else {
              this.selectedUserId.next(users.length > 0 ? users[0].id : 'all');
            }
          } else {
            this.selectedUserId.next(users.length > 0 ? users[0].id : 'all');
          }
        }
      )
    );
    this.subscriptions.push(
      combineLatest(
        this.selectedProjectCountryId,
        this.selectedUserId
      ).pipe(
        filter(([countryId, userId]) => !!countryId && !!userId),
        distinctUntilChanged(
          (a, b) => a[0] === b[0] && a[1] === b[1]
        ),
        switchMap(([countryId, userId]) => {
          let params = (new HttpParams())
            .append('RegionSearch[project_country_id]', countryId)
            .append('fields', 'id,name')
            .append('per-page', '250')
            .append('sort', 'name');
          if (userId !== 'all') {
            params = params.append('RegionSearch[user_id]', userId);
          }
          return this.http.get(
            this.environmentApiBasepoint + 'region',
            {params: params}
          );
        })
      ).pipe(
        map((regions: Array<any>) => [
            {
              id: 'all',
              name: this.translates.instant('All regions')
            },
            ...regions
          ]
        )
      )
        .subscribe(
          (regions: any) => {
            this.regions.next(regions);
            const previouslySelectedRegionId = this.selectedRegionId.getValue();
            if (previouslySelectedRegionId !== 'all') {
              const previouslySelectedRegion = regions.find(region => region.id === previouslySelectedRegionId);
              if (previouslySelectedRegion) {
                this.selectedRegionId.next(previouslySelectedRegionId);
              } else {
                this.selectedRegionId.next(regions.length > 0 ? regions[0].id : 'all');
              }
            } else {
              this.selectedRegionId.next(regions.length > 0 ? regions[0].id : 'all');
            }
          }
        )
    );
    this.selectedUserRole = combineLatest(
      this.selectedUserId,
      this.users
    ).pipe(
      map(([selectedOrganisationId, user]) => {
        const findRole = user.find(item => selectedOrganisationId === item.id);
        return findRole ? findRole.role : null;
      })
    );
  }

  onDestroy() {
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }
}
